import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  InputNumber,
  Select,
  Slider,
  Switch,
  Button,
  Modal,
  Tabs,
  Tag,
  Divider,
} from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  DownloadOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  FileTextOutlined,
  CheckOutlined,
  EditOutlined,
  LockOutlined,
  LoginOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  SALARY_LIB,
  DEFAULT_SALARY_INPUTS,
  computeSalaryMechanism,
  exportSalaryExcel,
  fmtMoney,
  fmtTrieu,
  fmtPercent,
  r0,
} from '../data/pharmacySalaryData.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { savePendingLead, clearPendingLead, resendPendingLead } from '../../../utils/pendingLead.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { useToolAccess } from '../hooks/useToolAccess.js';
import { ToolPurchaseModal, ToolPreviewBanner } from '../components/ToolPurchase.jsx';
import '../styles/pharmacy-salary-tool.css';

const TOOL_SLUG = 'co-che-luong-thuong';

const LOCAL_STORAGE_INPUTS_KEY = 'thethao_salary_tool_inputs';

// Lead gửi lỗi được giữ tạm và tự gửi lại khi mở lại công cụ (HT-06 D-02, YC-TOOL-09)
const LOCAL_STORAGE_PENDING_LEAD = 'luong_pending_lead';

export function PharmacySalaryToolPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Mở công cụ: nếu còn lead gửi lỗi lần trước thì gửi lại (chạy ngầm, không báo người dùng)
  useEffect(() => {
    resendPendingLead(LOCAL_STORAGE_PENDING_LEAD, (p) => pharmacyToolApi.submitSalaryToolLead(p));
  }, []);

  // Chưa mua công cụ: chế độ xem thử — chỉ xem số liệu mẫu và kết quả mẫu, sửa số liệu / tải Excel thì mở modal mua
  const { access } = useToolAccess();
  const toolAccess = access?.[TOOL_SLUG];
  const isPreview = !!toolAccess && !toolAccess.hasAccess;
  const [buyModalOpen, setBuyModalOpen] = useState(false);

  const [savedInputs, setSavedInputs] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_INPUTS_KEY);
      if (saved) {
        return { ...DEFAULT_SALARY_INPUTS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('Lỗi đọc inputs từ localStorage:', e);
    }
    return DEFAULT_SALARY_INPUTS;
  });
  const inputs = isPreview ? DEFAULT_SALARY_INPUTS : savedInputs;
  const setInputs = (updater) => {
    if (isPreview) {
      setBuyModalOpen(true);
      return;
    }
    setSavedInputs(updater);
  };

  const [hasCalculated, setHasCalculated] = useState(false);
  const [authModalVisible, setAuthModalVisible] = useState(false);

  const resultRef = useRef(null);

  // Lưu nháp inputs vào localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_INPUTS_KEY, JSON.stringify(savedInputs));
    } catch (e) { }
  }, [savedInputs]);

  // Động cơ tính toán trung tâm
  const R = useMemo(() => {
    return computeSalaryMechanism(inputs);
  }, [inputs]);

  // Có ít nhất 1 người ở vị trí tuyến sau (kho vận, kế toán) thì mới chỉnh mức hưởng tuyến sau ở Bước 4
  const hasBackOffice = SALARY_LIB.viTri.some(
    (v) => v.loai === 'ho' && (inputs.positions[v.id]?.n ?? v.defaultN) > 0
  );

  // Cập nhật giá trị một trường
  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Cập nhật số lượng hoặc bậc của vị trí nhân sự
  const handlePositionChange = (posId, key, value) => {
    setInputs((prev) => ({
      ...prev,
      positions: {
        ...prev.positions,
        [posId]: {
          ...(prev.positions[posId] || {}),
          [key]: value,
        },
      },
    }));
  };

  // Nút hành động áp dụng mức chi trả vừa túi quầy
  const applySuggestedPayRate = () => {
    if (R.mucVua) {
      handleInputChange('mct', R.mucVua);
      toast.success(`Đã tự động chỉnh mức chi trả về ${R.mucVua}% an toàn cho quầy!`);
    }
  };

  // Bấm nút "Xây cơ chế lương thưởng"
  // Đang xem thử (chưa mua): bấm xây là mở modal mua, không cho xem kết quả
  const handleRunBuild = () => {
    if (isPreview) {
      setBuyModalOpen(true);
      return;
    }
    if (!user || !user.id) {
      setAuthModalVisible(true);
      return;
    }
    setHasCalculated(true);
    setTimeout(() => {
      if (resultRef.current) {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  // Bấm nút "Tải File Excel" ở khu vực kết quả — dùng chung cơ chế yêu cầu đăng nhập
  const handleDownloadExcelClick = () => {
    if (isPreview) {
      setBuyModalOpen(true);
      return;
    }
    if (!user || !user.id) {
      setAuthModalVisible(true);
      return;
    }
    try {
      exportSalaryExcel(R);
      toast.success('Đã tải xuống file Excel co-che-luong-thuong-quay-thuoc.xlsx!');

      const leadPayload = {
        name: user.fullName || '',
        phone: user.phone || '',
        store: user.pharmacyName || '',
        doanhThuTrieu: r0(R.I.dt / 1e6),
        bienLaiGop: +R.bien.toFixed(1),
        soNguoi: R.soNguoi,
        moHinh: R.I.mo,
        quyLuongTrieu: r0(R.quyTong / 1e6),
        quyTrenDoanhThu: +R.tlDT.toFixed(1),
        quyTrenLaiGop: +R.tlLG.toFixed(1),
        mucRuiRo: R.mucRuiRo,
      };
      pharmacyToolApi
        .submitSalaryToolLead(leadPayload)
        .then(() => clearPendingLead(LOCAL_STORAGE_PENDING_LEAD))
        .catch((err) => {
          console.warn('Gửi lead lương thưởng lỗi:', err);
          // Lỗi mạng / máy chủ: giữ lại để lần sau mở công cụ gửi tiếp. Dữ liệu sai (400) thì bỏ.
          if (err?.statusCode !== 400) savePendingLead(LOCAL_STORAGE_PENDING_LEAD, leadPayload);
        });
    } catch (err) {
      toast.error(err.message || 'Lỗi tạo file Excel');
    }
  };

  // Xác nhận chuyển sang trang đăng nhập, giữ lại đường dẫn để tự quay lại
  const handleConfirmLogin = () => {
    setAuthModalVisible(false);
    navigate('/login', {
      state: {
        from: {
          pathname: '/tools/co-che-luong-thuong?autostart=true',
        },
      },
    });
  };

  // Tự động chạy lại việc xây cơ chế nếu vừa đăng nhập xong và quay về từ trang login
  useEffect(() => {
    const shouldAutoStart = searchParams.get('autostart') === 'true';
    if (shouldAutoStart && user && user.id && !hasCalculated) {
      handleRunBuild();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  // Đèn màu & trạng thái
  const verdictClass = R.mucRuiRo === 'khoe' ? 'good' : R.mucRuiRo === 'canh_bao' ? 'warn' : 'bad';
  const verdictBadge =
    R.mucRuiRo === 'khoe'
      ? 'Cơ chế gánh được'
      : R.mucRuiRo === 'canh_bao'
        ? 'Gánh được nhưng sát trần'
        : 'Quỹ lương vượt sức quầy';

  let verdictTitle = '';
  let verdictDesc = '';
  if (R.mucRuiRo === 'khoe') {
    verdictTitle = `Quầy trả được quỹ lương ${fmtTrieu(R.quyTong, 1)} mỗi tháng`;
    verdictDesc = `Chiếm ${fmtPercent(R.tlDT)} doanh thu và ${fmtPercent(R.tlLG)} lãi gộp, còn lại ${fmtTrieu(
      R.loiNhuan,
      1
    )} lợi nhuận trước thuế sau khi trừ các chi phí cố định bạn đã khai. Cơ chế này an toàn và bền vững.`;
  } else if (R.mucRuiRo === 'canh_bao') {
    verdictTitle = R.thieuDT
      ? 'Chưa đủ số liệu doanh thu để kết luận'
      : `Quỹ lương ${fmtTrieu(R.quyTong, 1)}/tháng đang nằm sát trần rủi ro`;
    verdictDesc = R.thieuDT
      ? `Quỹ lương cứng đang là ${fmtTrieu(
        R.quyCung,
        1
      )}/tháng, cần bổ sung doanh thu và biên lãi gộp để đánh giá chính xác.`
      : `Chiếm ${fmtPercent(R.tlDT)} doanh thu và ${fmtPercent(
        R.tlLG
      )} lãi gộp. Vẫn trả được nhưng hụt doanh số 1 tháng là hết thặng dư dự phòng. Hãy xem đề xuất điều chỉnh bên dưới.`;
  } else {
    verdictTitle = `Quỹ lương ${fmtTrieu(R.quyTong, 1)} đang vượt sức chi trả của quầy`;
    verdictDesc = `Chiếm ${fmtPercent(R.tlDT)} doanh thu và ${fmtPercent(
      R.tlLG
    )} lãi gộp, cao hơn ngưỡng an toàn tài chính ngành dược lẻ. Cần cân đối trước khi ký ban hành.`;
  }

  return (
    <div className="pharmacy-salary-container">
      {/* 1. Header Banner */}
      <section className="salary-hero-banner">
        <div className="salary-hero-inner">
          <div style={{ marginBottom: 14 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ
            </Link>
          </div>
          <Row gutter={[24, 24]} align="middle" justify="space-between">
            <Col xs={24} md={14}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <Tag color="#0d9488" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  CƠ CHẾ LƯƠNG THƯỞNG QUẦY THUỐC
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>
                  Xây Dựng Thang Bảng Lương 2P/3P & Quỹ Thưởng Lãi Gộp
                </span>
              </div>
              <h1 className="salary-hero-title">
                Công Cụ Xây <em>Cơ Chế Lương Thưởng</em> Cho Quầy Thuốc
              </h1>
              <p className="salary-hero-desc">
                Nhập doanh thu, cơ cấu ba nhóm hàng và đội hình thực tế. Công cụ sẽ tự động thiết lập thang bảng lương 5
                bậc, quỹ thưởng sinh ra từ lãi gộp thực chiến, giả lập bảng lương từng người và xuất file Excel có công thức
                sống.
              </p>
              <div className="salary-privacy-pill">
                <CheckCircleOutlined />
                <span>
                  <b>An toàn 100%:</b> Số liệu tính toán trực tiếp trên trình duyệt của bạn, xuất Excel tại chỗ.
                </span>
              </div>
            </Col>

            {/* Hình ảnh cơ chế lương thưởng quầy thuốc */}
            <Col xs={24} md={10}>
              <div
                style={{
                  position: 'relative',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  aspectRatio: '16/9',
                  background: 'rgba(0, 0, 0, 0.1)',
                }}
              >
                <img
                  src="/images/pharmacy-salary-bonus-mechanism.jpg"
                  alt="Cơ chế lương thưởng quầy thuốc nhà thuốc"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </section>

      <div className="salary-main-wrap">
        {isPreview && (
          <ToolPreviewBanner onBuy={() => setBuyModalOpen(true)}>
            <b>Bạn đang xem thử với số liệu mẫu.</b> Mua công cụ để nhập số liệu quầy của bạn, xây cơ chế lương thưởng,
            xem bảng lương từng người và tải file Excel 7 sheet.
          </ToolPreviewBanner>
        )}
        {/* 2. Builder Inputs Section */}
        <section className="salary-input-section">
          {/* Bước 1: Bối cảnh quầy */}
          <div className="salary-panel">
            <div className="salary-panel-header">
              <div className="salary-panel-header-left">
                <span className="salary-step-tag">Bước 1</span>
                <h2 className="salary-panel-title">Bối cảnh quầy thuốc</h2>
              </div>
              <span className="salary-panel-note">Căn cứ tính toán ca kíp và lương sàn tối thiểu</span>
            </div>
            <div className="salary-panel-body">
              <Row gutter={[20, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <div className="salary-field-label">Doanh thu trung bình / tháng</div>
                  <InputNumber
                    style={{ width: '100%' }}
                    size="large"
                    min={0}
                    step={10}
                    value={inputs.dt}
                    onChange={(val) => handleInputChange('dt', val)}
                    addonAfter="triệu đ"
                  />
                  <div className="salary-field-hint">Doanh số bình quân 3–6 tháng gần nhất</div>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <div className="salary-field-label">Số ngày mở cửa / tháng</div>
                  <InputNumber
                    style={{ width: '100%' }}
                    size="large"
                    min={20}
                    max={31}
                    value={inputs.ngay}
                    onChange={(val) => handleInputChange('ngay', val)}
                    addonAfter="ngày"
                  />
                  <div className="salary-field-hint">Thường mở đủ 30 ngày</div>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <div className="salary-field-label">Số giờ mở cửa / ngày</div>
                  <InputNumber
                    style={{ width: '100%' }}
                    size="large"
                    min={6}
                    max={24}
                    step={0.5}
                    value={inputs.gio}
                    onChange={(val) => handleInputChange('gio', val)}
                    addonAfter="giờ"
                  />
                  <div className="salary-field-hint">Dùng để tính định biên nhân sự phủ ca</div>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <div className="salary-field-label">Vùng lương tối thiểu</div>
                  <Select
                    style={{ width: '100%' }}
                    size="large"
                    value={inputs.vung}
                    onChange={(val) => handleInputChange('vung', val)}
                    options={[
                      { value: '1', label: 'Vùng I — 5.310.000 đ' },
                      { value: '2', label: 'Vùng II — 4.730.000 đ' },
                      { value: '3', label: 'Vùng III — 4.140.000 đ' },
                      { value: '4', label: 'Vùng IV — 3.700.000 đ' },
                    ]}
                  />
                  <div className="salary-field-hint">Nghị định 293/2025/NĐ-CP (từ 01/01/2026)</div>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <div className="salary-field-label">Chủ quầy có đứng bán không</div>
                  <Select
                    style={{ width: '100%' }}
                    size="large"
                    value={inputs.chu}
                    onChange={(val) => handleInputChange('chu', val)}
                    options={[
                      { value: 'full', label: 'Đứng quầy toàn thời gian' },
                      { value: 'part', label: 'Có mặt vài buổi trong tuần' },
                      { value: 'no', label: 'Không đứng quầy, thuê quản lý' },
                    ]}
                  />
                  <div className="salary-field-hint">Ảnh hưởng tới phân bổ quỹ thưởng bán hàng</div>
                </Col>

                <Col xs={24} sm={12} md={8}>
                  <div className="salary-field-label">Chi phí cố định khác / tháng</div>
                  <InputNumber
                    style={{ width: '100%' }}
                    size="large"
                    min={0}
                    step={5}
                    value={inputs.chiPhi}
                    onChange={(val) => handleInputChange('chiPhi', val)}
                    addonAfter="triệu đ"
                  />
                  <div className="salary-field-hint">Mặt bằng, điện nước, phần mềm...</div>
                </Col>
              </Row>
            </div>
          </div>

          {/* Bước 2: Cơ cấu 3 nhóm hàng và lãi gộp */}
          <div className="salary-panel">
            <div className="salary-panel-header">
              <div className="salary-panel-header-left">
                <span className="salary-step-tag">Bước 2</span>
                <h2 className="salary-panel-title">Cơ cấu ba nhóm hàng & Lãi gộp</h2>
              </div>
              <span className="salary-panel-note">Gốc rễ tài chính sinh ra quỹ thưởng</span>
            </div>
            <div className="salary-panel-body">
              <div className="salary-group-row head">
                <span>Nhóm hàng</span>
                <span>Tỷ trọng DT</span>
                <span>Lãi gộp</span>
                <span>Ra tiền mỗi tháng</span>
              </div>

              {/* Nhóm 1 */}
              <div className="salary-group-row">
                <div className="salary-group-title-col">
                  <div className="salary-group-title">Nhóm 1 · Hàng thông dụng (Khách hỏi đúng tên)</div>
                  <div className="salary-group-desc">
                    Hạ sốt, cảm cúm, kháng sinh phổ thông, bông băng... Lãi mỏng, ai bán cũng được.
                  </div>
                </div>
                <div>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={100}
                    value={inputs.w1}
                    onChange={(val) => handleInputChange('w1', val)}
                    addonAfter="%"
                  />
                </div>
                <div>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={90}
                    step={0.5}
                    value={inputs.m1}
                    onChange={(val) => handleInputChange('m1', val)}
                    addonAfter="%"
                  />
                </div>
                <div className="salary-group-out">
                  <b>{fmtTrieu(R.dtN[0], 0)}</b> doanh thu → <b>{fmtTrieu(R.lgN[0], 1)}</b> lãi gộp
                  <br />
                  trích thưởng <b>{fmtTrieu(R.thuongN[0], 2)}</b>
                </div>
              </div>

              {/* Nhóm 2 */}
              <div className="salary-group-row">
                <div className="salary-group-title-col">
                  <div className="salary-group-title">Nhóm 2 · Hàng tư vấn (Hỏi bệnh, đổi phác đồ)</div>
                  <div className="salary-group-desc">
                    Hàng cần dược sĩ khai thác triệu chứng, giải thích hướng dẫn. Chỗ tay nghề tạo ra tiền.
                  </div>
                </div>
                <div>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={100}
                    value={inputs.w2}
                    onChange={(val) => handleInputChange('w2', val)}
                    addonAfter="%"
                  />
                </div>
                <div>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={90}
                    step={0.5}
                    value={inputs.m2}
                    onChange={(val) => handleInputChange('m2', val)}
                    addonAfter="%"
                  />
                </div>
                <div className="salary-group-out">
                  <b>{fmtTrieu(R.dtN[1], 0)}</b> doanh thu → <b>{fmtTrieu(R.lgN[1], 1)}</b> lãi gộp
                  <br />
                  trích thưởng <b>{fmtTrieu(R.thuongN[1], 2)}</b>
                </div>
              </div>

              {/* Nhóm 3 */}
              <div className="salary-group-row">
                <div className="salary-group-title-col">
                  <div className="salary-group-title">Nhóm 3 · Hàng chiến lược (TPCN, Dược mỹ phẩm)</div>
                  <div className="salary-group-desc">
                    Hàng nhãn riêng, độc quyền, bổ não, xương khớp... Biên lãi cao nhất, cần chăm sóc khách.
                  </div>
                </div>
                <div>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={100}
                    value={inputs.w3}
                    onChange={(val) => handleInputChange('w3', val)}
                    addonAfter="%"
                  />
                </div>
                <div>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={90}
                    step={0.5}
                    value={inputs.m3}
                    onChange={(val) => handleInputChange('m3', val)}
                    addonAfter="%"
                  />
                </div>
                <div className="salary-group-out">
                  <b>{fmtTrieu(R.dtN[2], 0)}</b> doanh thu → <b>{fmtTrieu(R.lgN[2], 1)}</b> lãi gộp
                  <br />
                  trích thưởng <b>{fmtTrieu(R.thuongN[2], 2)}</b>
                </div>
              </div>

              {/* Summary tổng nhóm */}
              <div className="salary-group-summary">
                <span>
                  Tổng tỷ trọng DT:{' '}
                  <b style={{ color: Math.abs(R.tongW - 100) > 0.5 ? '#b91c1c' : '#065f46' }}>
                    {fmtPercent(R.tongW, 0)}
                  </b>
                  {Math.abs(R.tongW - 100) > 0.5 && ' (Nên cân đối tròn 100%)'}
                </span>
                <span>•</span>
                <span>
                  Biên lãi gộp bình quân cả quầy: <b>{fmtPercent(R.bien, 1)}</b>
                </span>
                <span>•</span>
                <span>
                  Tổng lãi gộp sinh ra: <b>{fmtTrieu(R.lg, 1)}</b>/tháng
                </span>
              </div>
            </div>
          </div>

          {/* Bước 3: Đội hình đang có */}
          <div className="salary-panel">
            <div className="salary-panel-header">
              <div className="salary-panel-header-left">
                <span className="salary-step-tag">Bước 3</span>
                <h2 className="salary-panel-title">Đội hình nhân sự thực tế</h2>
              </div>
              <span className="salary-panel-note">Định biên vị trí và xếp bậc bình quân</span>
            </div>
            <div className="salary-panel-body">
              <div className="salary-staff-row head">
                <span>Vị trí nhân sự</span>
                <span>Số người</span>
                <span>Bậc bình quân</span>
                <span>Xem trước lương vị trí</span>
              </div>

              {SALARY_LIB.viTri.map((v) => {
                const pos = inputs.positions[v.id] || { n: v.defaultN, bac: v.defaultBac };
                const rut = inputs.mo === '3P' ? SALARY_LIB.heSo3P : 1;
                // Cùng công thức P1 trong computeSalaryMechanism: kẹp sàn lương tối thiểu vùng rồi làm tròn đến nghìn đồng
                const p1Preview =
                  pos.n > 0
                    ? Math.round(
                      Math.max(R.LTTV, R.LTTV * v.hs * SALARY_LIB.bacHS[pos.bac - 1] * (inputs.mct / 100) * rut) / 1000
                    ) * 1000
                    : 0;

                return (
                  <div className="salary-staff-row" key={v.id}>
                    <div className="salary-staff-name">
                      <b>{v.ten}</b>
                      <i>{v.mo}</i>
                    </div>
                    <div>
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        max={20}
                        value={pos.n}
                        onChange={(val) => handlePositionChange(v.id, 'n', val || 0)}
                      />
                    </div>
                    <div>
                      <Select
                        style={{ width: '100%' }}
                        value={pos.bac}
                        onChange={(val) => handlePositionChange(v.id, 'bac', val)}
                        options={[
                          { value: 1, label: 'Bậc 1 (<1 năm)' },
                          { value: 2, label: 'Bậc 2 (1-2 năm)' },
                          { value: 3, label: 'Bậc 3 (2-3.5 năm)' },
                          { value: 4, label: 'Bậc 4 (3.5-5 năm)' },
                          { value: 5, label: 'Bậc 5 (>5 năm)' },
                        ]}
                      />
                    </div>
                    <div className="salary-staff-preview">
                      {pos.n > 0 ? (
                        <span style={{ fontSize: 13, color: '#334155' }}>
                          Lương bậc {pos.bac}: <b style={{ color: '#0d9488' }}>{fmtMoney(p1Preview)}</b>
                        </span>
                      ) : (
                        <span style={{ fontSize: 12.5, color: '#94a3b8' }}>Chưa bố trí</span>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="salary-field-hint" style={{ marginTop: 14 }}>
                💡 <b>Nguyên tắc xếp bậc:</b> Bậc 1: dưới 1 năm • Bậc 2: 1–2 năm • Bậc 3: 2–3,5 năm • Bậc 4: 3,5–5 năm •
                Bậc 5: trên 5 năm. Công cụ sẽ tự rải bậc quanh mức bình quân bạn chọn để tạo bảng lương sát thực tế.
              </div>
            </div>
          </div>

          {/* Bước 4: Chính sách trả lương */}
          <div className="salary-panel">
            <div className="salary-panel-header">
              <div className="salary-panel-header-left">
                <span className="salary-step-tag">Bước 4</span>
                <h2 className="salary-panel-title">Chính sách trả lương & Đòn bẩy thưởng</h2>
              </div>
              <span className="salary-panel-note">Mô hình 2P/3P và tỷ lệ trích thưởng</span>
            </div>
            <div className="salary-panel-body">
              {/* Chọn mô hình 2P hoặc 3P */}
              <div className="salary-mode-grid">
                <div
                  className={`salary-mode-card ${inputs.mo === '2P' ? 'active' : ''}`}
                  onClick={() => handleInputChange('mo', '2P')}
                >
                  <h4>
                    Mô hình 2P
                    {inputs.mo === '2P' && <CheckCircleOutlined style={{ color: '#0d9488' }} />}
                  </h4>
                  <p>
                    <b>P1 Lương vị trí</b> (trả theo chỗ ngồi và bậc) + <b>P2 Thưởng hiệu quả</b> (theo lãi gộp bán
                    được).
                  </p>
                  <div className="fit">Phù hợp với quầy dưới 4 người, chủ trực tiếp quản lý, cơ chế gọn gàng.</div>
                </div>

                <div
                  className={`salary-mode-card ${inputs.mo === '3P' ? 'active' : ''}`}
                  onClick={() => handleInputChange('mo', '3P')}
                >
                  <h4>
                    Mô hình 3P (Chuẩn khuyến nghị)
                    {inputs.mo === '3P' && <CheckCircleOutlined style={{ color: '#0d9488' }} />}
                  </h4>
                  <p>
                    <b>P1 Vị trí</b> + <b>P2 Năng lực</b> (phụ cấp CCHN, bằng đại học/cao đẳng, thâm niên) +{' '}
                    <b>P3 Hiệu quả</b>.
                  </p>
                  <div className="fit">
                    Phù hợp quầy từ 4 người, có nhiều bằng cấp khác nhau, cần giữ chân dược sĩ giỏi lâu dài.
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} md={12}>
                  <div
                    className={`salary-toggle-box ${inputs.kpi ? 'active' : ''}`}
                    onClick={() => handleInputChange('kpi', !inputs.kpi)}
                  >
                    <Switch checked={inputs.kpi} onChange={(val) => handleInputChange('kpi', val)} />
                    <div>
                      <span className="salary-toggle-title">Gắn thẻ điểm KPI hàng tháng vào thưởng</span>
                      <span className="salary-toggle-desc">
                        Nhân hệ số KPI theo 5 tiêu chí chuyên môn và vận hành, tránh chạy doanh số mà bỏ quên vệ sinh,
                        hạn dùng và đơn thuốc.
                      </span>
                    </div>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div
                    className={`salary-toggle-box ${inputs.cn ? 'active' : ''}`}
                    onClick={() => handleInputChange('cn', !inputs.cn)}
                  >
                    <Switch checked={inputs.cn} onChange={(val) => handleInputChange('cn', val)} />
                    <div>
                      <span className="salary-toggle-title">Phần mềm quầy tách được doanh số từng người</span>
                      <span className="salary-toggle-desc">
                        Bật nếu phần mềm ghi nhận người bán trên đơn. Khi đó thưởng tính thẳng theo lãi gộp cá nhân.
                        Nếu tắt, thưởng chia theo hệ số vị trí × bậc × KPI.
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Divider style={{ margin: '20px 0' }} />

              {/* Thanh trượt tỷ lệ trích thưởng */}
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    color: '#0d9488',
                    marginBottom: 4,
                  }}
                >
                  Tỷ lệ trích thưởng trên lãi gộp 3 nhóm hàng (Đòn bẩy tư vấn)
                </div>
                <p className="salary-field-hint" style={{ marginBottom: 16 }}>
                  Trích trên lãi gộp chứ không trích trên doanh thu, giúp chống bán phá giá và hướng nhân viên tích cực tư
                  vấn nhóm 2 và nhóm 3.
                </p>

                <Row gutter={[24, 16]}>
                  <Col xs={24} md={8}>
                    <div className="salary-field-label">
                      <span>Nhóm 1 · Thông dụng</span>
                      <b>{inputs.rate1}%</b>
                    </div>
                    <Slider
                      min={0}
                      max={25}
                      step={0.5}
                      value={inputs.rate1}
                      onChange={(val) => handleInputChange('rate1', val)}
                    />
                  </Col>

                  <Col xs={24} md={8}>
                    <div className="salary-field-label">
                      <span>Nhóm 2 · Tư vấn</span>
                      <b>{inputs.rate2}%</b>
                    </div>
                    <Slider
                      min={0}
                      max={35}
                      step={0.5}
                      value={inputs.rate2}
                      onChange={(val) => handleInputChange('rate2', val)}
                    />
                  </Col>

                  <Col xs={24} md={8}>
                    <div className="salary-field-label">
                      <span>Nhóm 3 · Chiến lược / TPCN</span>
                      <b>{inputs.rate3}%</b>
                    </div>
                    <Slider
                      min={0}
                      max={40}
                      step={0.5}
                      value={inputs.rate3}
                      onChange={(val) => handleInputChange('rate3', val)}
                    />
                  </Col>
                </Row>
              </div>

              {/* Mức hưởng tuyến sau & Mức chi trả */}
              <Row gutter={[24, 16]} style={{ marginTop: 18 }}>
                <Col xs={24} md={12}>
                  <div className="salary-field-label">
                    <span>Mức hưởng của tuyến sau so với tuyến bán hàng</span>
                    <b>{hasBackOffice ? `${inputs.ht}%` : '—'}</b>
                  </div>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={inputs.ht}
                    disabled={!hasBackOffice}
                    onChange={(val) => handleInputChange('ht', val)}
                  />
                  <div className="salary-field-hint">
                    {hasBackOffice ? (
                      <>
                        Kho vận và kế toán hưởng {fmtPercent(R.tyTrongHT * 100, 0)} quỹ thưởng để giữ date, chống hao
                        hụt và khớp công nợ.
                      </>
                    ) : (
                      <>
                        Quầy chưa có nhân viên kho vận hay kế toán ở Bước 3 nên toàn bộ quỹ thưởng dành cho tuyến bán
                        hàng. Thêm người ở 2 vị trí này để chỉnh mức hưởng.
                      </>
                    )}
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="salary-field-label">
                    <span>Mức chi trả so với chuẩn tham chiếu</span>
                    <b>{inputs.mct}%</b>
                  </div>
                  <Slider
                    min={70}
                    max={120}
                    step={1}
                    value={inputs.mct}
                    onChange={(val) => handleInputChange('mct', val)}
                  />
                  <div className="salary-field-hint">
                    Kéo xuống nếu quầy ở huyện/xã có mặt bằng lương thấp. Lương cứng luôn kẹp trên sàn tối thiểu vùng.
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {/* Action Button: Xây cơ chế */}
          <div className="salary-actions-bar">
            <Button
              type="primary"
              size="large"
              icon={<ThunderboltOutlined />}
              onClick={handleRunBuild}
              style={{
                background: '#005537',
                borderColor: '#005537',
                height: 48,
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 15,
                padding: '0 28px',
              }}
            >
              Xây Cơ Chế Lương Thưởng
            </Button>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {user && user.id
                ? 'Kết quả tính toán hiển thị chi tiết ngay bên dưới'
                : 'Cần đăng nhập để xem kết quả phân tích chi tiết'}
            </span>
          </div>
        </section>

        {/* 3. Output Diagnosis & Results Section */}
        {hasCalculated && (
          <section ref={resultRef} style={{ marginTop: 24 }}>
            {/* Header Kết Quả */}
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#0d9488',
                }}
              >
                Kết quả phân tích
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '4px 0 0' }}>
                Cơ Chế Lương Thưởng Chi Tiết Cho Quầy Thuốc
              </h2>
            </div>

            {/* Verdict Card */}
            <div className="salary-verdict-card">
              <span className={`salary-status-badge ${verdictClass}`}>
                <span className={`salary-dot ${verdictClass}`}></span>
                {verdictBadge}
              </span>
              <h3 className="salary-verdict-title">{verdictTitle}</h3>
              <p className="salary-verdict-desc">{verdictDesc}</p>

              {/* Gợi ý hạ mức chi trả nếu quỹ lương vượt ngưỡng */}
              {R.mucRuiRo !== 'khoe' && !R.thieuDT && R.soNguoi > 0 && (
                <div className="salary-fit-action">
                  {R.mucVua >= 72 && R.mucVua < inputs.mct ? (
                    <>
                      <div style={{ flex: 1, minWidth: 280, fontSize: 13.5, color: '#44403c' }}>
                        <b>Mức chi trả vừa túi quầy gợi ý: {R.mucVua}%</b> (thay vì {inputs.mct}% đang đặt). Ở mức này,
                        quỹ lương sẽ kéo về khoảng {fmtPercent(SALARY_LIB.nguong.lgTot)} lãi gộp — điểm cân bằng an toàn
                        để quầy tái đầu tư hàng.
                      </div>
                      <Button
                        type="dashed"
                        style={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600 }}
                        onClick={applySuggestedPayRate}
                      >
                        Áp dụng {R.mucVua}% rồi tính lại
                      </Button>
                    </>
                  ) : (
                    <div style={{ fontSize: 13.5, color: '#991b1b' }}>
                      <b>Hạ lương khó giải quyết dứt điểm:</b> Với chi phí bình quân {fmtTrieu(R.chiPhiMoiNguoi, 1)}
                      /người, doanh thu {fmtTrieu(R.I.dt, 0)} chỉ nuôi nổi khoảng <b>{R.nguoiToiDa} người</b> thay vì{' '}
                      {R.soNguoi} người. Cần cân đối lại ca kíp hoặc kéo doanh thu lên tối thiểu{' '}
                      <b>{fmtTrieu(R.dtCan, 0)}</b>/tháng.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 8 Financial Health Metrics Grid */}
            <div className="salary-kpi-grid">
              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Quỹ lương đề xuất</span>
                <span className="salary-kpi-tile-val">{fmtTrieu(R.quyTong, 1)}</span>
                <span className="salary-kpi-tile-sub">
                  Cứng {fmtTrieu(R.quyCung, 1)} + Thưởng {fmtTrieu(R.quyThuong, 1)}
                </span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Quỹ lương / Doanh thu</span>
                <span className="salary-kpi-tile-val">
                  <span className={`salary-dot ${R.den.dt}`}></span>
                  {fmtPercent(R.tlDT)}
                </span>
                <span className="salary-kpi-tile-sub">Ngưỡng an toàn &lt; {SALARY_LIB.nguong.dtTot}%</span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Quỹ lương / Lãi gộp</span>
                <span className="salary-kpi-tile-val">
                  <span className={`salary-dot ${R.den.lg}`}></span>
                  {fmtPercent(R.tlLG)}
                </span>
                <span className="salary-kpi-tile-sub">Ngưỡng an toàn &lt; {SALARY_LIB.nguong.lgTot}%</span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Doanh thu mỗi người</span>
                <span className="salary-kpi-tile-val">
                  <span className={`salary-dot ${R.den.dtNguoi}`}></span>
                  {fmtTrieu(R.dtNguoi, 0)}
                </span>
                <span className="salary-kpi-tile-sub">
                  Chuẩn lành mạnh &gt; {SALARY_LIB.nguong.dtNguoiTot} tr/người
                </span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Lãi gộp mỗi tháng</span>
                <span className="salary-kpi-tile-val">{fmtTrieu(R.lg, 1)}</span>
                <span className="salary-kpi-tile-sub">Biên bình quân {fmtPercent(R.bien)}</span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Thu nhập bình quân</span>
                <span className="salary-kpi-tile-val">{fmtTrieu(R.tnBQ, 1)}</span>
                <span className="salary-kpi-tile-sub">Thưởng chiếm {fmtPercent(R.tlThuong)} thu nhập</span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Doanh thu hoà vốn</span>
                <span className="salary-kpi-tile-val">{fmtTrieu(R.hoaVon, 0)}</span>
                <span className="salary-kpi-tile-sub">Đủ trả lương cứng & chi phí cố định</span>
              </div>

              <div className="salary-kpi-tile">
                <span className="salary-kpi-tile-lbl">Lợi nhuận còn lại</span>
                <span className="salary-kpi-tile-val">
                  <span className={`salary-dot ${R.den.loiNhuan}`}></span>
                  {fmtTrieu(R.loiNhuan, 1)}
                </span>
                <span className="salary-kpi-tile-sub">Sau lương & {fmtTrieu(R.I.chiPhi, 0)} phí cố định</span>
              </div>
            </div>

            {/* Nguồn sinh quỹ thưởng từ 3 nhóm hàng */}
            <div className="salary-panel">
              <div className="salary-panel-header">
                <div className="salary-panel-header-left">
                  <h2 className="salary-panel-title">Cơ cấu nguồn sinh quỹ thưởng tháng</h2>
                </div>
                <span className="salary-panel-note">Trích trực tiếp trên lãi gộp thực tế của từng nhóm</span>
              </div>
              <div className="salary-panel-body">
                {(() => {
                  const maxTh = Math.max(R.thuongN[0], R.thuongN[1], R.thuongN[2], 1);
                  const labels = [
                    { name: 'Nhóm 1 · Thông dụng', color: '#64748b' },
                    { name: 'Nhóm 2 · Tư vấn', color: '#0d9488' },
                    { name: 'Nhóm 3 · Chiến lược', color: '#f59e0b' },
                  ];
                  return (
                    <div>
                      {labels.map((item, idx) => {
                        const pctOfFund = R.quyThuongGoc > 0 ? (R.thuongN[idx] / R.quyThuongGoc) * 100 : 0;
                        const barWidth = (R.thuongN[idx] / maxTh) * 100;
                        return (
                          <div className="salary-fund-bar" key={idx}>
                            <div className="salary-fund-bar-label">
                              <b>{item.name}</b>
                              <span>
                                {fmtPercent(R.wn[idx] * 100, 0)} doanh thu • Lãi gộp {fmtTrieu(R.lgN[idx], 1)} • Trích{' '}
                                {fmtPercent(inputs[`rate${idx + 1}`], 1)} →{' '}
                                <b style={{ color: '#0f172a' }}>{fmtTrieu(R.thuongN[idx], 2)}</b> (
                                {fmtPercent(pctOfFund, 0)} quỹ thưởng)
                              </span>
                            </div>
                            <div className="salary-fund-bar-track">
                              <div
                                className="salary-fund-bar-fill"
                                style={{ width: `${barWidth}%`, backgroundColor: item.color }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}

                      <div
                        style={{
                          borderTop: '1px solid #f1f5f9',
                          paddingTop: 12,
                          marginTop: 12,
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 13.5,
                        }}
                      >
                        <b>Tổng quỹ thưởng tháng sinh ra:</b>
                        <b style={{ color: '#005537', fontSize: 15 }}>
                          {fmtTrieu(R.quyThuongGoc, 2)} (Chia {fmtPercent((1 - R.tyTrongHT) * 100, 0)} bán hàng +{' '}
                          {fmtPercent(R.tyTrongHT * 100, 0)} kho & kế toán)
                        </b>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Bảng lương giả lập từng người */}
            <div className="salary-panel">
              <div className="salary-panel-header">
                <div className="salary-panel-header-left">
                  <TeamOutlined style={{ color: '#0d9488', fontSize: 18 }} />
                  <h2 className="salary-panel-title">Bảng lương giả lập từng nhân sự</h2>
                </div>
                <Tag color="cyan">
                  Mô hình {inputs.mo} {inputs.kpi && '+ KPI'}
                </Tag>
              </div>
              <div className="salary-panel-body" style={{ padding: 0 }}>
                <div className="salary-table-wrap">
                  <table className="salary-table">
                    <thead>
                      <tr>
                        <th>Vị trí</th>
                        <th>Bậc</th>
                        {inputs.cn && <th>Doanh thu cá nhân</th>}
                        {inputs.kpi && <th>Điểm KPI</th>}
                        {inputs.kpi && <th>Hệ số KPI</th>}
                        <th>P1 Vị trí</th>
                        {inputs.mo === '3P' && <th>P2 Năng lực</th>}
                        <th>{inputs.mo === '3P' ? 'P3' : 'P2'} Hiệu quả</th>
                        <th>Tổng thu nhập</th>
                      </tr>
                    </thead>
                    <tbody>
                      {R.ns.map((p, idx) => (
                        <tr key={idx}>
                          <td>
                            <b>{p.viTri}</b>
                            <div style={{ fontSize: 11.5, color: '#64748b' }}>
                              {p.cchn ? 'Có CCHN • ' : ''}
                              {SALARY_LIB.bangTen[p.bang] || ''} • {p.thamNien} năm
                            </div>
                          </td>
                          <td>{p.bac}</td>
                          {inputs.cn && <td>{p.banHS > 0 ? fmtTrieu(p.dtCaNhan, 0) : '—'}</td>}
                          {inputs.kpi && <td>{p.kpiDiem}</td>}
                          {inputs.kpi && <td>{p.hsKPI.toFixed(2).replace('.', ',')}</td>}
                          <td>{r0(p.p1).toLocaleString('vi-VN')}</td>
                          {inputs.mo === '3P' && <td>{r0(p.p2).toLocaleString('vi-VN')}</td>}
                          <td>{r0(p.p3).toLocaleString('vi-VN')}</td>
                          <td style={{ fontWeight: 800, color: '#047857' }}>
                            {r0(p.tong).toLocaleString('vi-VN')}
                          </td>
                        </tr>
                      ))}
                      <tr className="total-row">
                        <td>TỔNG QUỸ LƯƠNG THÁNG</td>
                        <td></td>
                        {inputs.cn && <td></td>}
                        {inputs.kpi && <td></td>}
                        {inputs.kpi && <td></td>}
                        <td>{r0(R.ns.reduce((sum, p) => sum + p.p1, 0)).toLocaleString('vi-VN')}</td>
                        {inputs.mo === '3P' && (
                          <td>{r0(R.ns.reduce((sum, p) => sum + p.p2, 0)).toLocaleString('vi-VN')}</td>
                        )}
                        <td>{r0(R.ns.reduce((sum, p) => sum + p.p3, 0)).toLocaleString('vi-VN')}</td>
                        <td style={{ color: '#005537', fontSize: 15 }}>
                          {r0(R.quyTong).toLocaleString('vi-VN')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ padding: '14px 20px', background: '#f8fafc', fontSize: 12.5, color: '#64748b' }}>
                  💡 <i>Ghi chú:</i> Bảng nhân sự trên là số liệu giả lập phân bổ theo đúng số lượng và bậc bạn đã chọn.
                  Trong file Excel, cột doanh thu cá nhân là ô màu vàng — bạn có thể copy doanh thu thực tế của từng bạn
                  từ phần mềm bán hàng để bảng tự động tính lại 100%.
                </div>
              </div>
            </div>

            {/* Tabs: Quy chế viết sẵn + Thẻ điểm KPI + Cảnh báo */}
            <div className="salary-panel">
              <div className="salary-panel-body" style={{ padding: '12px 20px 20px' }}>
                <Tabs
                  defaultActiveKey="quy-che"
                  items={[
                    {
                      key: 'quy-che',
                      label: (
                        <span>
                          <FileTextOutlined /> Quy Chế Ban Hành Viết Sẵn (7 Điều)
                        </span>
                      ),
                      children: (
                        <div style={{ fontSize: 14, lineHeight: 1.7, color: '#334155' }}>
                          <h4 style={{ fontSize: 15, fontWeight: 800, color: '#005537' }}>
                            ĐIỀU 1. THU NHẬP MỘT NGƯỜI GỒM NHỮNG GÌ
                          </h4>
                          <p>
                            Thu nhập tháng = <b>P1 Lương vị trí</b> +{' '}
                            {inputs.mo === '3P' ? (
                              <>
                                <b>P2 Phụ cấp năng lực</b> + <b>P3 Thưởng hiệu quả</b>
                              </>
                            ) : (
                              <b>P2 Thưởng hiệu quả</b>
                            )}
                            .
                          </p>
                          <p>
                            • <b>P1 — Lương vị trí:</b> Trả theo chỗ ngồi và bậc, tính từ mức lương tối thiểu vùng{' '}
                            {fmtMoney(R.LTTV)} ({SALARY_LIB.vungTen[inputs.vung]}), nhân hệ số vị trí, hệ số bậc và mức
                            chi trả {inputs.mct}%. Luôn đảm bảo không thấp hơn sàn tối thiểu vùng.
                          </p>
                          {inputs.mo === '3P' && (
                            <p>
                              • <b>P2 — Phụ cấp năng lực:</b> Trả cho chứng chỉ hành nghề (
                              {fmtMoney(SALARY_LIB.phucCap.cchn)}), bằng đại học dược (
                              {fmtMoney(SALARY_LIB.phucCap.dh)}), cao đẳng ({fmtMoney(SALARY_LIB.phucCap.cd)}), trung cấp
                              ({fmtMoney(SALARY_LIB.phucCap.tc)}) và thâm niên ({fmtMoney(SALARY_LIB.phucCap.tnNam)}/năm,
                              tối đa {fmtMoney(SALARY_LIB.phucCap.tnMax)}).
                            </p>
                          )}
                          <p>
                            • <b>{inputs.mo === '3P' ? 'P3' : 'P2'} — Thưởng hiệu quả:</b> Trích từ lãi gộp thực tế của
                            từng nhóm hàng (không trích trên doanh thu).
                          </p>

                          <Divider style={{ margin: '14px 0' }} />

                          <h4 style={{ fontSize: 15, fontWeight: 800, color: '#005537' }}>
                            ĐIỀU 2. CÔNG THỨC SINH QUỸ THƯỞNG
                          </h4>
                          <p>
                            Quỹ thưởng tháng = Lãi gộp Nhóm 1 × <b>{fmtPercent(inputs.rate1, 1)}</b> + Lãi gộp Nhóm 2 ×{' '}
                            <b>{fmtPercent(inputs.rate2, 1)}</b> + Lãi gộp Nhóm 3 × <b>{fmtPercent(inputs.rate3, 1)}</b>
                            .<br />
                            Với quy mô hiện tại: {fmtTrieu(R.lgN[0], 1)} × {inputs.rate1}% + {fmtTrieu(R.lgN[1], 1)} ×{' '}
                            {inputs.rate2}% + {fmtTrieu(R.lgN[2], 1)} × {inputs.rate3}% ={' '}
                            <b>{fmtTrieu(R.quyThuongGoc, 2)}</b> quỹ thưởng.
                          </p>

                          <Divider style={{ margin: '14px 0' }} />

                          <h4 style={{ fontSize: 15, fontWeight: 800, color: '#005537' }}>
                            ĐIỀU 3. HAI KHOÁ AN TOÀN BẢO VỆ QUỸ THƯỞNG
                          </h4>
                          <p>
                            <b>• Khoá 1 (Sản lượng):</b> Doanh thu tháng đạt từ 85% chỉ tiêu trở lên. Không đạt thì quỹ
                            thưởng phạt nhân hệ số 0,7.
                          </p>
                          <p>
                            <b>• Khoá 2 (Chất lượng lãi):</b> Biên lãi gộp thực tế không thấp hơn kế hoạch quá 1,5 điểm
                            %, tức không dưới <b>{fmtPercent(Math.max(0, R.bien - 1.5))}</b>. Không đạt thì quỹ thưởng
                            nhân hệ số 0,8.
                          </p>
                        </div>
                      ),
                    },
                    {
                      key: 'kpi-cards',
                      label: (
                        <span>
                          <CheckOutlined /> Thẻ Điểm KPI (100 Điểm)
                        </span>
                      ),
                      children: (
                        <div>
                          <Row gutter={[20, 20]}>
                            <Col xs={24} md={8}>
                              <div
                                style={{
                                  background: '#f8fafc',
                                  padding: 16,
                                  borderRadius: 8,
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <h4 style={{ fontSize: 14, fontWeight: 800, color: '#065f46' }}>
                                  Tuyến Bán Hàng & Tư Vấn
                                </h4>
                                <ul style={{ paddingLeft: 18, margin: '10px 0 0', fontSize: 13, lineHeight: 1.6 }}>
                                  {SALARY_LIB.kpiBan.map((k, i) => (
                                    <li key={i}>
                                      {k[0]} (<b>{k[1]}%</b>)
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Col>

                            <Col xs={24} md={8}>
                              <div
                                style={{
                                  background: '#f8fafc',
                                  padding: 16,
                                  borderRadius: 8,
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <h4 style={{ fontSize: 14, fontWeight: 800, color: '#065f46' }}>
                                  Tuyến Kho Vận & Soạn Hàng
                                </h4>
                                <ul style={{ paddingLeft: 18, margin: '10px 0 0', fontSize: 13, lineHeight: 1.6 }}>
                                  {SALARY_LIB.kpiKho.map((k, i) => (
                                    <li key={i}>
                                      {k[0]} (<b>{k[1]}%</b>)
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Col>

                            <Col xs={24} md={8}>
                              <div
                                style={{
                                  background: '#f8fafc',
                                  padding: 16,
                                  borderRadius: 8,
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <h4 style={{ fontSize: 14, fontWeight: 800, color: '#065f46' }}>
                                  Tuyến Kế Toán & Sổ Sách
                                </h4>
                                <ul style={{ paddingLeft: 18, margin: '10px 0 0', fontSize: 13, lineHeight: 1.6 }}>
                                  {SALARY_LIB.kpiKeToan.map((k, i) => (
                                    <li key={i}>
                                      {k[0]} (<b>{k[1]}%</b>)
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Col>
                          </Row>

                          <div
                            style={{
                              marginTop: 16,
                              padding: 12,
                              background: '#f0fdf4',
                              borderRadius: 8,
                              fontSize: 12.5,
                              color: '#166534',
                            }}
                          >
                            <b>Thang quy đổi hệ số nhân vào thưởng:</b> Từ 95 điểm: 1,30 (Xuất sắc) • 90–94 điểm: 1,15
                            (Tốt) • 80–89 điểm: 1,00 (Đạt) • 70–79 điểm: 0,85 • 60–69 điểm: 0,65 • Dưới 60: 0,00.
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'canh-bao',
                      label: (
                        <span>
                          <ExclamationCircleOutlined /> Cảnh Báo Cần Xử Lý ({R.canhBao.length})
                        </span>
                      ),
                      children: (
                        <div>
                          {R.canhBao.map((c, i) => (
                            <div className={`salary-alert-box ${c.t}`} key={i}>
                              <span className="salary-alert-icon">{c.t === 'good' ? '✓' : '!'}</span>
                              <div className="salary-alert-content">
                                <h4>{c.h}</h4>
                                <p>{c.p}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            {/* Bottom Actions & Support CTA */}
            <div className="salary-actions-bar">
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleDownloadExcelClick}
                style={{
                  background: '#005537',
                  borderColor: '#005537',
                  height: 48,
                  borderRadius: 10,
                  fontWeight: 700,
                  padding: '0 24px',
                }}
              >
                Tải Xuống File Excel 7 Sheet
              </Button>
              <Button
                size="large"
                icon={<EditOutlined />}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{ height: 48, borderRadius: 10, fontWeight: 600 }}
              >
                Sửa Lại Tham Số
              </Button>
            </div>

            {/* Đồng hành cùng Thế Thảo Pharma CTA */}
            <div className="salary-cta-box">
              <h3>Cần rà soát cơ chế lương thưởng trước khi ký ban hành?</h3>
              <p>
                Đội ngũ Thế Thảo Pharma đang đồng hành hỗ trợ hơn 20.000 nhà thuốc trên toàn quốc. Bạn có thể gửi file Excel vừa tải về qua Zalo để chuyên gia soát giúp hệ số, quỹ trích và tính pháp lý hoàn toàn miễn phí.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Button
                  type="primary"
                  href="https://zalo.me/0812539539"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: '#005537', borderColor: '#047857', borderRadius: 8, fontWeight: 700 }}
                >
                  Nhắn Zalo Chuyên Gia: 0812.539.539
                </Button>
                <Button
                  href="https://chothuoc247.vn"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ borderRadius: 8, fontWeight: 600 }}
                >
                  Ghé Thăm Chợ Thuốc 247
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* 4. Modal Yêu Cầu Đăng Nhập Để Xây Cơ Chế Lương Thưởng */}
      <Modal
        open={authModalVisible}
        onCancel={() => setAuthModalVisible(false)}
        footer={null}
        centered
        width={480}
        styles={{ body: { padding: '32px 28px' } }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#ecfdf5',
              border: '2px solid #a7f3d0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)',
            }}
          >
            <LockOutlined style={{ fontSize: 30, color: '#005537' }} />
          </div>

          <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
            Yêu Cầu Đăng Nhập
          </h3>

          <p style={{ fontSize: 14.5, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
            Bạn cần <strong>đăng nhập tài khoản</strong> để xây và xem kết quả cơ chế lương thưởng cho quầy thuốc.
          </p>

          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '14px 16px',
              textAlign: 'left',
              marginBottom: 24,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
              Quyền lợi khi đăng nhập:
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              <li>Xem đầy đủ kết quả phân tích quỹ lương, quỹ thưởng và bảng lương từng người.</li>
              <li>Lưu lại tham số quầy thuốc để chỉnh sửa và tính lại bất cứ lúc nào.</li>
              <li>Nhận hỗ trợ rà soát cơ chế trực tiếp từ chuyên gia Thế Thảo Pharma.</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button
              size="large"
              onClick={() => setAuthModalVisible(false)}
              style={{
                borderRadius: 10,
                fontWeight: 600,
                flex: 1,
                height: 44,
              }}
            >
              Để sau
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<LoginOutlined />}
              onClick={handleConfirmLogin}
              style={{
                backgroundColor: '#005537',
                borderColor: '#005537',
                borderRadius: 10,
                fontWeight: 700,
                flex: 1,
                height: 44,
                boxShadow: '0 4px 12px rgba(0, 85, 55, 0.25)',
              }}
            >
              Đăng Nhập Ngay
            </Button>
          </div>

          <div style={{ marginTop: 16, fontSize: 13, color: '#64748b' }}>
            Chưa có tài khoản?{' '}
            <Link
              to="/register"
              onClick={() => setAuthModalVisible(false)}
              style={{ color: '#005537', fontWeight: 700, textDecoration: 'underline' }}
            >
              Đăng ký miễn phí
            </Link>
          </div>
        </div>
      </Modal>

      <ToolPurchaseModal open={buyModalOpen} onClose={() => setBuyModalOpen(false)} plans={toolAccess?.requiredPlans || []} />
    </div>
  );
}
